import React, { useState } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { isFunction } from 'lodash-es';
import { useLatest } from 'ahooks';
import { Dropdown, List } from 'tea-component';
import SortByIcon from '../SortByIcon';
import { getSortInteractionMode, getNextSortTypeIntelligence } from './utils';
import { SortType } from '@home/enums/common';
import { SortInteractionMode } from './enums';

import './index.scss';

/**
 * 通用配置
 */
export interface ISortByCommonConfigs<T = any> {
  /**
   * 是否禁用，默认值为false
   */
  disabled?: boolean;
  /**
   * 是否开启多字段排序，默认值为false
   */
  useMultiSort?: boolean;
  /**
   * 是否开启重置为无排序状态，默认值为false
   */
  useResetSortTypeNone?: boolean;
  /**
   * 排序Comparator（用于前端排序，后端排序时不需要Comparator）
   */
  comparator?: IComparator<T> | null;
}

/**
 * SortBy组件props类型
 */
export interface ISortByProps<T = any> extends IBaseProps, ISortByCommonConfigs<T> {
  /**
   * 字段
   */
  field: string;
  /**
   * 字段标题
   * 1、字符串
   * 2、renderer函数
   */
  title: ISortByTitle<T>;
  /**
   * 排序交互模式
   * 1、非数组或空数组时，排序交互模式为SortInteractionMode.DirectMode模式
   * 2、数组并且数组长度大于0时，排序交互模式为SortInteractionMode.OptionsMode模式
   */
  options?: Readonly<ISortOption>[] | null;
  /**
   * 当前排序状态列表
   * 1、useMultiSort为false时，列表仅包含一个元素，即当前排序字段的排序状态
   * 2、useMultiSort为true时，列表包含多个元素，即当前参与排序的多个字段对应的排序状态
   */
  sorts?: Readonly<ISortState<T>>[] | null;
  /**
   * 排序的回调
   * @param sorts 新的排序状态列表
   * @param option 本次排序操作对应的排序扩展项
   */
  onSortsChange?: (sorts: ISortState<T>[], option?: ISortOption | null) => void;
}

export default function SortBy<T = any>(props: ISortByProps<T>) {
  const {
    style,
    className,
    field,
    title,
    disabled = false,
    useMultiSort = false,
    useResetSortTypeNone = false,
    options,
    comparator,
    sorts,
    onSortsChange,
  } = props;

  const sort = sorts?.find((item) => field === item?.field);
  const sortField = sort?.field;
  const sortType = sort?.type ?? SortType.None;
  const sortOption = sort?.option;
  const isFieldMatched = field === sortField;
  const sortByIcon = <SortByIcon disabled={disabled} forceSortTypeNone={!isFieldMatched} sortType={sortType} />;

  // 排序交互模式
  const sortInteractionMode = getSortInteractionMode(options);

  const { formatMessage } = useIntl();

  const getNextSortType = useLatest(getNextSortTypeIntelligence(useResetSortTypeNone));

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleSortsChange = (option?: Readonly<ISortOption>) => {
    if (disabled) return;
    // 当前排序状态列表
    const currentSorts = Array.isArray(sorts) ? [...sorts] : [];
    // 当前字段的排序状态索引
    const existingSortIndex = currentSorts.findIndex((item) => field === item.field);
    // 当前字段的排序状态
    const existingSort = existingSortIndex === -1 ? null : currentSorts[existingSortIndex];
    // 当前字段的新排序方式
    const nextSortType = (() => {
      const isOptionChanged = !!sortOption && !!option && sortOption.field !== option.field;
      return isOptionChanged ? getNextSortType.current() : getNextSortType.current(existingSort?.type);
    })();
    // 当前字段的新排序状态
    const nextSort: ISortState<T> = {
      ...existingSort,
      field,
      type: nextSortType,
      comparator,
      option,
    };

    /** ******************************************************** 单字段排序逻辑 ******************************************************** **/
    if (!useMultiSort) {
      if (nextSortType === SortType.None) {
        onSortsChange?.([], option);
      } else {
        onSortsChange?.([nextSort], option);
      }
      setDropdownOpen(false);
      return;
    }
    /** ******************************************************** 单字段排序逻辑 ******************************************************** **/

    /** ******************************************************** 多字段排序逻辑 ******************************************************** **/
    // 当前字段的新排序状态列表
    const newSorts = (() => {
      // 当前字段本次排序操作为取消排序
      if (nextSortType === SortType.None) {
        return currentSorts.filter((item) => field !== item?.field);
      }

      // 当前字段没有参与排序，并且本次排序操作为参与排序（升序或者降序）
      if (existingSortIndex === -1) return [...currentSorts, nextSort];

      // 当前字段有参与排序，并且本次排序操作为参与排序（升序或者降序）
      return [...currentSorts.slice(0, existingSortIndex), nextSort, ...currentSorts.slice(existingSortIndex + 1)];
    })();

    onSortsChange?.(newSorts, option);

    setDropdownOpen(false);
    /** ******************************************************** 多字段排序逻辑 ******************************************************** **/
  };

  const renderSortTitle = () => {
    if (isFunction(title)) return title(sort, sorts);
    return title;
  };

  // 渲染排序控件
  const renderSortControl = () => {
    if (!field) return null;

    switch (sortInteractionMode) {
      case SortInteractionMode.DirectMode: {
        return (
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleSortsChange();
            }}
          >
            {sortByIcon}
          </div>
        );
      }
      case SortInteractionMode.OptionsMode: {
        return (
          <Dropdown
            className="dbi-sort-by-dropdown"
            boxClassName="dbi-sort-by-dropdown-overlay"
            appearance="pure"
            trigger="hover"
            disabled={disabled}
            open={dropdownOpen}
            onOpenChange={setDropdownOpen}
            button={sortByIcon}
          >
            <List className="dbi-sort-by-dropdown-overlay__sort-by-list" type="option">
              <div className="dbi-sort-by-dropdown-overlay__header">{formatMessage({ id: 'dbi_sortby' })}</div>
              {options!.map((option) => {
                const optionField = option?.field || '';
                const optionText = option?.text || '';
                return (
                  <List.Item
                    key={optionField}
                    className="dbi-sort-by-dropdown-overlay__sort-by-item"
                    onClick={(e) => {
                      e?.stopPropagation();
                      handleSortsChange(option);
                    }}
                  >
                    <SortByIcon
                      disabled={disabled}
                      forceSortTypeNone={!isFieldMatched || optionField !== sortOption?.field}
                      sortType={sortType}
                    />
                    <span>{optionText}</span>
                  </List.Item>
                );
              })}
            </List>
          </Dropdown>
        );
      }
      default: {
        return null;
      }
    }
  };

  return (
    <div
      style={style}
      className={classnames('dbi-sort-by', className, {
        'dbi-sort-by--disabled': disabled,
        'dbi-sort-by--direct-mode': sortInteractionMode === SortInteractionMode.DirectMode,
        'dbi-sort-by--highlight': isFieldMatched && sortType !== SortType.None,
      })}
    >
      {/* 标题 */}
      <div className="dbi-sort-by__title-wrapper">{renderSortTitle()}</div>
      {/* 排序控件 */}
      <div className="dbi-sort-by__sort-icon-wrapper">{renderSortControl()}</div>
    </div>
  );
}
